/**
 * Product Configurator — Nomadart
 * Drawer-based UI: format tabs + expandable rows for size/frame/PP.
 * Reads JSON config from metaobjects (via Liquid), handles dynamic pricing,
 * and multi-item cart add (artwork + frame product + PP product).
 */

class ProductConfigurator extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.getAttribute('section-id');
    this.productUrl = this.getAttribute('product-url');
    this.optionFormatName = this.getAttribute('option-format-name') || 'Formato';
    this.optionSizeName = this.getAttribute('option-size-name') || 'Tamaño';
    this._labelNoFrame = this.getAttribute('label-no-frame') || 'Sin marco';
    this._labelNoPassepartout = this.getAttribute('label-no-passepartout') || 'Sin paspartú';

    // Settings from block attributes
    this._showBreakdown = this.getAttribute('show-breakdown') !== 'false';
    this._sizeDrawerOpen = this.getAttribute('size-drawer-open') !== 'false';
    this._showSizePrices = this.getAttribute('show-size-prices') !== 'false';
    this._hideUnavailableSizes = this.getAttribute('hide-unavailable-sizes') === 'true';
    this._layout = this.getAttribute('layout') || 'accordion';
    this._openSheetName = null;

    // Reference to the native product form for dispatching cart events
    this._nativeFormId = this.getAttribute('form-id') || null;

    this.config = null;
    this.variants = [];
    this._openDrawer = null;
    this._activeFrameCategory = null;

    this.state = {
      format: null,
      size: null,
      frame: null,
      passepartout: null,
      artworkVariantId: null,
      frameVariantId: null,
      ppVariantId: null,
      artworkPrice: 0,
      framePrice: 0,
      ppPrice: 0
    };

    this._refs = {};
    this._adding = false;
  }

  connectedCallback() {
    this._loadData();
    if (this.config) this._normalizeFormats();
    this._detectOrientation();
    this._cacheRefs();
    if (this._layout === 'sheet') this._portalSheets();
    this._initFormat();
    this._bindEvents();
    this._preloadImages();
  }

  disconnectedCallback() {
    if (this._portal) {
      this._portal.remove();
      this._portal = null;
    }
  }

  /**
   * Detect product orientation.
   * Priority: 1) artwork-orientation attribute (from product metafield)
   *           2) size-based square detection (all NxN)
   *           3) defaults to 'vertical'
   */
  _detectOrientation() {
    // 1) Read explicit orientation from product metafield (via Liquid attribute)
    const attr = (this.getAttribute('artwork-orientation') || '').toLowerCase().trim();
    const map = { vertical: 'vertical', horizontal: 'horizontal', square: 'square', cuadrada: 'square' };
    if (map[attr]) {
      this._orientation = map[attr];
      return;
    }

    this._orientation = 'vertical'; // default

    if (!this.config || !this.config.formats || !this.config.formats.length) return;

    // 2) Check if ALL sizes across all formats are NxN (square)
    const allSizes = this.config.formats.flatMap(f => f.sizes || []);
    if (allSizes.length) {
      const normSize = s => s.replace(/[\u00d7\u2715\u2716*]/g, 'x').replace(/\s+/g, '').toLowerCase();
      const isSquare = allSizes.every(s => {
        const parts = normSize(s).split('x');
        return parts.length === 2 && parts[0] === parts[1];
      });
      if (isSquare) {
        this._orientation = 'square';
        return;
      }
    }
  }

  /* ============================================================
     DATA LOADING
     ============================================================ */

  _loadData() {
    const configEl = document.getElementById(`configurator-config-${this.sectionId}`);
    const variantsEl = document.getElementById(`configurator-variants-${this.sectionId}`);

    if (configEl) {
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[Configurator] Failed to parse config:', e);
        return;
      }
    }

    if (variantsEl) {
      try {
        this.variants = JSON.parse(variantsEl.textContent);
      } catch (e) {
        console.error('[Configurator] Failed to parse variants:', e);
      }
    }
  }

  /* ============================================================
     DATA NORMALIZATION
     ============================================================ */

  _normalizeFormats() {
    if (!this.config || !this.config.formats) return;

    const normStr = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

    // Build unique format option values from variants options[0]
    // These are in the store's primary language (not translated), e.g. "Lámina", "Lienzo"
    const formatOptionValues = [];
    const seenFov = new Set();
    for (const v of this.variants) {
      const val = v.options[0];
      if (val && !seenFov.has(val)) { seenFov.add(val); formatOptionValues.push(val); }
    }
    const normFov = formatOptionValues.map(v => normStr(v));

    // TYPE RANK from systemHandle — the metaobject system handle is NEVER translated
    // e.g. "lamina" or "lamina-1" → rank 0 (first), "lienzo" → rank 1
    const typeRank = handle => {
      const n = normStr(handle || '').replace(/-\d+$/, ''); // strip trailing digits
      if (n === 'lamina' || n.includes('lamina')) return 0;
      if (n === 'lienzo' || n.includes('lienzo')) return 1;
      return 2;
    };

    // Sort: always lamina-type first, driven by systemHandle (language-agnostic)
    this.config.formats.sort((a, b) => typeRank(a.systemHandle) - typeRank(b.systemHandle));

    // Assign _variantOptionValue using multiple strategies (fallback chain):
    // 1. variantIndex from Liquid (works when option values are untranslated and match fc.name)
    // 2. systemHandle normalized match against v.options[0] (primary language)
    // 3. Normalized name match (handles accent-only differences)
    // 4. Name as-is (last resort)
    this.config.formats.forEach((fmt, i) => {
      const idx = (typeof fmt.variantIndex === 'number' && fmt.variantIndex >= 0) ? fmt.variantIndex : -1;
      if (idx >= 0 && idx < formatOptionValues.length) {
        fmt._variantOptionValue = formatOptionValues[idx];
        return;
      }
      // systemHandle-based match (e.g. "lamina" matches "Lámina" in primary language)
      const handleBase = normStr(fmt.systemHandle || '').replace(/-\d+$/, '');
      let match = handleBase ? formatOptionValues.find((_, fi) => normFov[fi] === handleBase) : null;
      // Normalized name match (fallback: same word, different locale/accent)
      if (!match) {
        const normName = normStr(fmt.name);
        match = formatOptionValues.find((_, fi) => normFov[fi] === normName);
      }
      // Strategy 4: positional fallback — after typeRank sort, formats[i] aligns with
      // formatOptionValues[i] when the product variant order matches the desired format order
      if (!match && i < formatOptionValues.length) {
        match = formatOptionValues[i];
      }
      fmt._variantOptionValue = match || fmt.name;
    });

    this.config.formats.forEach(format => {
      // Sizes: auto-detect from product variants, fallback to metaobject config
      const metaobjectSizes = this._normalizeSizeList(format.sizes);
      const detectedSizes = this._extractSizesFromVariants(format._variantOptionValue || format.name);

      if (detectedSizes.length) {
        // Use detected sizes; if metaobject provided ordering, respect it
        if (metaobjectSizes.length) {
          const detectedSet = new Set(detectedSizes);
          const ordered = metaobjectSizes.filter(s => detectedSet.has(s));
          const extra = detectedSizes.filter(s => !ordered.includes(s));
          format.sizes = [...ordered, ...extra];
        } else {
          format.sizes = detectedSizes;
        }
      } else {
        // Fallback to metaobject sizes (backward compat)
        format.sizes = metaobjectSizes;
      }

      // Generate handle from name: "Lámina" → "lamina"
      if (!format.handle) {
        format.handle = format.name
          .toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }

      // Auto-prepend "Sin marco" virtual option
      if (!format.frames) format.frames = [];
      const hasSinMarco = format.frames.some(f => f.handle === 'sin-marco');
      if (!hasSinMarco) {
        format.frames.unshift({
          name: this._labelNoFrame,
          handle: 'sin-marco',
          category: 'none',
          colorHex: 'transparent',
          swatchImage: '',
          overlayImage: '',
          productId: null,
          variants: []
        });
      }

      // Auto-prepend "Sin paspartú" virtual option
      if (!format.passepartouts) format.passepartouts = [];
      if (format.hasPassepartout) {
        const hasSinPp = format.passepartouts.some(p => p.handle === 'sin-paspartu');
        if (!hasSinPp) {
          format.passepartouts.unshift({
            name: this._labelNoPassepartout,
            handle: 'sin-paspartu',
            colorHex: 'transparent',
            overlayImage: '',
            productId: null,
            variants: []
          });
        }
      }

      // Derive frameCategories from frames' category field
      const cats = [];
      const seen = new Set();
      for (const frame of format.frames) {
        if (frame.category && frame.category !== 'none' && !seen.has(frame.category)) {
          seen.add(frame.category);
          cats.push(frame.category);
        }
      }
      format.frameCategories = cats;
    });
  }

  /* ============================================================
     DOM REFERENCES — Drawer pattern
     ============================================================ */

  _cacheRefs() {
    this._refs = {
      formatTabs: this.querySelector('[data-format-tabs]'),
      formatLabel: this.querySelector('[data-format-label]'),
      formatImg: this.querySelector('[data-format-img-preview]'),
      // Size drawer
      sizeOptions: this.querySelector('[data-size-options]'),
      sizeLabel: this.querySelector('[data-size-label]'),
      basePrice: this.querySelector('[data-base-price]'),
      // Frame drawer
      frameOptions: this.querySelector('[data-frame-options]'),
      frameLabel: this.querySelector('[data-frame-label]'),
      frameSwatchPreview: this.querySelector('[data-frame-swatch-preview]'),
      frameSurcharge: this.querySelector('[data-frame-surcharge]'),
      frameCategories: this.querySelector('[data-frame-categories]'),
      // PP drawer
      ppOptions: this.querySelector('[data-pp-options]'),
      ppLabel: this.querySelector('[data-pp-label]'),
      ppSwatchPreview: this.querySelector('[data-pp-swatch-preview]'),
      ppSurcharge: this.querySelector('[data-pp-surcharge]'),
      ppSection: this.querySelector('[data-pp-section]'),
      // Breakdown
      breakdown: this.querySelector('[data-breakdown]'),
      breakdownArtRow: this.querySelector('[data-breakdown-art-row]'),
      breakdownArtPrice: this.querySelector('[data-breakdown-art-price]'),
      breakdownFrameRow: this.querySelector('[data-breakdown-frame-row]'),
      breakdownFrameLabel: this.querySelector('[data-breakdown-frame-label]'),
      breakdownFramePrice: this.querySelector('[data-breakdown-frame-price]'),
      breakdownPpRow: this.querySelector('[data-breakdown-pp-row]'),
      breakdownPpLabel: this.querySelector('[data-breakdown-pp-label]'),
      breakdownPpPrice: this.querySelector('[data-breakdown-pp-price]'),
      breakdownTotal: this.querySelector('[data-breakdown-total]'),
      // ATC
      atcBtn: this.querySelector('[data-atc-btn]'),
      atcText: this.querySelector('[data-atc-text]'),
      atcPrice: this.querySelector('[data-atc-price]')
    };

    // Native price block ref — update when breakdown is hidden
    const section = this.closest('.shopify-section--main-product') || this.closest('section');
    if (section) {
      this._nativeSalePrice = section.querySelector('.product-info__price sale-price');
      // Store original text to restore if needed
      if (this._nativeSalePrice) {
        this._nativePriceOriginal = this._nativeSalePrice.textContent.trim();
      }
    }

    // Mockup preview refs — on the PDP it lives in the gallery; inside the quick-buy drawer
    // it lives in the drawer header. Scope the lookup to the drawer first to avoid grabbing
    // another product card's mockup from the shared collection section.
    const mockupHost = this.closest('quick-buy-drawer') || section;
    if (mockupHost) {
      const mockup = mockupHost.querySelector('[data-configurator-mockup]');
      if (mockup) {
        this._mockup = mockup;
        this._mockupBase = mockup.querySelector('.pc-mockup__base');
        this._mockupFrame = mockup.querySelector('[data-mockup-frame]');
        this._mockupPp = mockup.querySelector('[data-mockup-pp]');
        this._mockupCarousel = mockupHost.querySelector('media-carousel');
        this._mockupScrolled = false;
        // Base image URLs for swap (4 states: sin-pp, con-pp, lienzo, lienzo-marco)
        if (this._mockupBase) {
          this._srcSinPp = this._mockupBase.dataset.srcSinPp || '';
          this._srcConPp = this._mockupBase.dataset.srcConPp || '';
          this._srcLienzo = this._mockupBase.dataset.srcLienzo || '';
          this._srcLienzoMarco = this._mockupBase.dataset.srcLienzoMarco || '';
          this._srcInitial = this._mockupBase.src;
        }
      }
    }

    // Quick-buy drawer header price — separate from the PDP native price block.
    // The header shows a static "from" price; keep it in sync with the configured total.
    const qbDrawer = this.closest('quick-buy-drawer');
    if (qbDrawer) {
      this._qbHeaderPrice = qbDrawer.querySelector('[slot="header"] sale-price');
    }
  }

  /* ============================================================
     INITIALIZATION
     ============================================================ */

  _initFormat() {
    if (!this.config || !this.config.formats.length) return;

    // Formats are already sorted by variantIndex from _normalizeFormats
    // Re-render tabs from JS to match sorted order
    this._renderFormatTabs();

    const firstFormat = this.config.formats[0];
    this._selectFormat(firstFormat.handle);
  }

  _renderFormatTabs() {
    if (this.config.formats.length <= 1) return;

    // Re-render main format tabs (accordion / non-sheet mode)
    if (this._refs.formatTabs) {
      const mainHtml = this.config.formats.map(f =>
        `<button type="button" class="pc__format-tab" data-format="${f.handle}" aria-pressed="false">${f.name}</button>`
      ).join('');
      this._refs.formatTabs.innerHTML = mainHtml;
    }

    // Re-order sheet format buttons to match sorted config.formats order
    // appendChild moves existing DOM nodes (preserves card image HTML)
    [this, this._portal].filter(Boolean).forEach(root => {
      const sheetOptions = root.querySelector('[data-sheet="format"] .pc__sheet-options');
      if (!sheetOptions) return;
      this.config.formats.forEach(f => {
        const btn = sheetOptions.querySelector(`[data-format="${f.handle}"]`);
        if (btn) sheetOptions.appendChild(btn);
      });
    });

  }

  /* ============================================================
     EVENT BINDING
     ============================================================ */

  _bindEvents() {
    // connectedCallback may fire multiple times (e.g. when the element is moved/re-inserted
    // by the quick-buy drawer). Listeners live on `this`/document and survive moves, so bind
    // only once to avoid duplicate handlers (which would double-toggle drawers).
    if (this._eventsBound) return;
    this._eventsBound = true;

    const clickHandler = (e) => {
      // Sheet: open panel
      const sheetOpen = e.target.closest('[data-sheet-open]');
      if (sheetOpen) {
        this._openSheet(sheetOpen.dataset.sheetOpen);
        return;
      }

      // Sheet: close button
      if (e.target.closest('[data-sheet-close]')) {
        this._closeSheet();
        return;
      }

      // Sheet: backdrop
      if (e.target.closest('[data-sheet-overlay]')) {
        this._closeSheet();
        return;
      }

      // Drawer toggle
      const toggle = e.target.closest('[data-drawer-toggle]');
      if (toggle) {
        this._toggleDrawer(toggle.dataset.drawerToggle);
        return;
      }

      // Format tab
      const formatTab = e.target.closest('[data-format]');
      if (formatTab) {
        this._selectFormat(formatTab.dataset.format);
        if (this._openSheetName === 'format') this._closeSheet();
        return;
      }

      // Size button
      const sizeBtn = e.target.closest('[data-size]');
      if (sizeBtn && !sizeBtn.disabled) {
        this._selectSize(sizeBtn.dataset.size);
        if (this._layout === 'sheet') this._closeSheet();
        return;
      }

      // Frame category pill
      const catBtn = e.target.closest('[data-frame-cat]');
      if (catBtn) {
        this._filterFramesByCategory(catBtn.dataset.frameCat);
        return;
      }

      // Frame swatch
      const frameSwatch = e.target.closest('[data-frame]');
      if (frameSwatch && !frameSwatch.disabled) {
        this._selectFrame(frameSwatch.dataset.frame);
        if (this._layout === 'sheet') this._closeSheet();
        return;
      }

      // PP swatch
      const ppSwatch = e.target.closest('[data-pp]');
      if (ppSwatch && !ppSwatch.disabled) {
        this._selectPp(ppSwatch.dataset.pp);
        if (this._layout === 'sheet') this._closeSheet();
        return;
      }

      // ATC
      const atcBtn = e.target.closest('[data-atc-btn]');
      if (atcBtn && !atcBtn.disabled) {
        this._addToCart();
        return;
      }
    };

    this.addEventListener('click', clickHandler);
    if (this._portal) this._portal.addEventListener('click', clickHandler);

    // Escape key closes sheet
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._openSheetName) this._closeSheet();
    });
  }

  /* ============================================================
     DRAWER TOGGLE
     ============================================================ */

  _toggleDrawer(name) {
    const drawer = this.querySelector(`[data-drawer="${name}"]`);
    if (!drawer) return;

    const panel = drawer.querySelector('[data-drawer-panel]');
    const trigger = drawer.querySelector('[data-drawer-toggle]');
    const isOpen = drawer.hasAttribute('data-open');

    // Close all drawers first
    this.querySelectorAll('[data-drawer]').forEach(d => {
      d.removeAttribute('data-open');
      const p = d.querySelector('[data-drawer-panel]');
      const t = d.querySelector('[data-drawer-toggle]');
      if (p) p.hidden = true;
      if (t) t.setAttribute('aria-expanded', 'false');
    });

    // Toggle target
    if (!isOpen) {
      drawer.setAttribute('data-open', '');
      if (panel) panel.hidden = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this._openDrawer = name;
    } else {
      this._openDrawer = null;
    }
  }

  /* ============================================================
     SHEET PORTAL — moves panels to <body> to escape stacking contexts
     ============================================================ */

  _portalSheets() {
    this._portal = document.createElement('div');
    this._portal.setAttribute('data-pc-portal', '');
    // Copy CSS variables from component so sheet panels inherit all settings
    const componentStyle = this.getAttribute('style');
    if (componentStyle) this._portal.setAttribute('style', componentStyle);
    document.body.appendChild(this._portal);

    const overlay = this.querySelector('[data-sheet-overlay]');
    if (overlay) this._portal.appendChild(overlay);

    this.querySelectorAll('[data-sheet]').forEach(panel => {
      this._portal.appendChild(panel);
    });
  }

  /* ============================================================
     SHEET OPEN / CLOSE
     ============================================================ */

  _openSheet(name) {
    this._closeSheet(false);

    const root = this._portal || this;
    const panel = root.querySelector(`[data-sheet="${name}"]`);
    if (!panel) return;

    panel.classList.add('pc__sheet-panel--open');
    panel.scrollTop = 0;

    const overlay = root.querySelector('[data-sheet-overlay]');
    if (overlay) overlay.classList.add('pc__sheet-overlay--open');

    this._openSheetName = name;
    document.body.style.overflow = 'hidden';
  }

  _closeSheet(restoreScroll = true) {
    const root = this._portal || this;
    root.querySelectorAll('.pc__sheet-panel--open').forEach(p => {
      p.classList.remove('pc__sheet-panel--open');
    });

    const overlay = root.querySelector('[data-sheet-overlay]');
    if (overlay) overlay.classList.remove('pc__sheet-overlay--open');

    this._openSheetName = null;

    if (restoreScroll) document.body.style.overflow = '';
  }

  _forceOpenDrawer(name) {
    // Close all drawers first, then unconditionally open the target
    this.querySelectorAll('[data-drawer]').forEach(d => {
      d.removeAttribute('data-open');
      const p = d.querySelector('[data-drawer-panel]');
      const t = d.querySelector('[data-drawer-toggle]');
      if (p) p.hidden = true;
      if (t) t.setAttribute('aria-expanded', 'false');
    });
    const drawer = this.querySelector(`[data-drawer="${name}"]`);
    if (!drawer) return;
    drawer.setAttribute('data-open', '');
    const panel = drawer.querySelector('[data-drawer-panel]');
    const trigger = drawer.querySelector('[data-drawer-toggle]');
    if (panel) panel.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    this._openDrawer = name;
  }

  _preloadImages() {
    if (!this.config || !this.config.formats) return;

    const urls = new Set();
    const fields = ['overlayImage', 'overlayImageH', 'overlayImageS', 'swatchImage'];
    const baseFields = ['baseOverlay', 'baseOverlayH', 'baseOverlayS'];

    this.config.formats.forEach(format => {
      baseFields.forEach(k => { if (format[k]) urls.add(format[k]); });
      if (format.cardImage) urls.add(format.cardImage);
      (format.frames || []).forEach(frame => {
        fields.forEach(k => { if (frame[k]) urls.add(frame[k]); });
      });
      (format.passepartouts || []).forEach(pp => {
        fields.forEach(k => { if (pp[k]) urls.add(pp[k]); });
      });
    });

    // Also preload mockup base states (already read by _cacheRefs)
    [this._srcSinPp, this._srcConPp, this._srcLienzo, this._srcLienzoMarco].forEach(src => {
      if (src) urls.add(src);
    });

    // Cache Image objects on the instance to prevent GC before load completes
    this._imgCache = [];
    urls.forEach(url => {
      const img = new Image();
      img.src = url;
      this._imgCache.push(img);
    });
  }

  _closeAllDrawers() {
    this.querySelectorAll('[data-drawer]').forEach(d => {
      d.removeAttribute('data-open');
      const p = d.querySelector('[data-drawer-panel]');
      const t = d.querySelector('[data-drawer-toggle]');
      if (p) p.hidden = true;
      if (t) t.setAttribute('aria-expanded', 'false');
    });
    this._openDrawer = null;
  }

  /* ============================================================
     FORMAT SELECTION
     ============================================================ */

  _selectFormat(handle) {
    const format = this.config.formats.find(f => f.handle === handle);
    if (!format) return;

    this.state.format = handle;
    this.state.size = null;
    this.state.frame = null;
    this.state.passepartout = null;
    this.state.frameVariantId = null;
    this.state.ppVariantId = null;
    this.state.framePrice = 0;
    this.state.ppPrice = 0;
    this._activeFrameCategory = null;

    this._updateFormatTabs(handle);
    this._renderSizes(format);
    this._renderFrameCategories(format);
    this._renderFrames(format);
    this._renderPassepartouts(format);

    // Auto-select first available size (respects hide_unavailable_sizes setting)
    const firstSize = this._hideUnavailableSizes
      ? format.sizes.find(s => {
          const v = this._findVariant(format._variantOptionValue || format.name, s);
          return v && v.available;
        })
      : format.sizes[0];
    if (firstSize) {
      this._selectSize(firstSize);
    }

    // Open size drawer based on setting (accordion only)
    if (this._sizeDrawerOpen && this._layout !== 'sheet') {
      this._forceOpenDrawer('size');
    }

    // Update mockup base (format may or may not support PP)
    this._updatePreview();
  }

  /* ============================================================
     SIZE SELECTION
     ============================================================ */

  _selectSize(size) {
    this.state.size = size;
    this._updateSizeButtons(size);

    if (this._layout === 'sheet') {
      const fmt = this._getCurrentFormat();
      if (fmt) {
        this._updateSheetFramePrices(fmt);
        this._updateSheetPpPrices(fmt);
      }
    }

    // Update trigger label
    if (this._refs.sizeLabel) {
      this._refs.sizeLabel.textContent = size + ' cm';
    }

    const format = this._getCurrentFormat();
    if (!format) return;

    // Update base price in trigger
    const variant = this._findVariant(format._variantOptionValue || format.name, size);
    if (this._refs.basePrice) {
      this._refs.basePrice.textContent = variant ? this._formatMoney(variant.price) : '';
    }

    // Auto-select default frame if not set (this triggers PP visibility via _updatePpVisibility)
    if (this.state.frame === null && format.frames.length) {
      this._selectFrame(format.frames[0].handle);
    } else {
      // Recalc prices for new size
      this._updateVariant();
      // Re-check PP visibility and recalc PP price
      const isNoFrame = this.state.frame === 'sin-marco';
      this._updatePpVisibility(format, isNoFrame);
    }

    this._updateVariant();
    this._updatePrice();
  }

  /* ============================================================
     FRAME SELECTION
     ============================================================ */

  _selectFrame(handle) {
    const format = this._getCurrentFormat();
    if (!format) return;

    const frame = format.frames.find(f => f.handle === handle);
    if (!frame) return;

    this.state.frame = handle;
    const isNoFrame = frame.handle === 'sin-marco';

    if (isNoFrame) {
      this.state.frameVariantId = null;
      this.state.framePrice = 0;
    } else {
      const fv = this._findAddonVariant(frame.variants, this.state.size);
      this.state.frameVariantId = fv ? fv.id : null;
      this.state.framePrice = fv ? fv.price : 0;
    }

    // Update swatch selection
    this._updateSwatchSelection(this._refs.frameOptions, handle, 'frame');

    // Update trigger label + swatch preview
    if (this._refs.frameLabel) {
      this._refs.frameLabel.textContent = frame.name;
    }
    this._updateSwatchPreview(this._refs.frameSwatchPreview, frame);

    // Update surcharge in trigger
    if (this._refs.frameSurcharge) {
      this._refs.frameSurcharge.textContent = this.state.framePrice > 0
        ? `+${this._formatMoney(this.state.framePrice)}`
        : '';
    }

    // Show/hide PP based on frame selection (PP only with a real frame)
    this._updatePpVisibility(format, isNoFrame);

    this._updatePrice();
    this._updatePreview();
  }

  /**
   * Show PP section only when format supports PP AND a real frame is selected.
   * If no frame → hide PP and reset PP state.
   */
  _updatePpVisibility(format, isNoFrame) {
    if (!this._refs.ppSection) return;

    const showPp = format.hasPassepartout && format.passepartouts.length > 0 && !isNoFrame;
    this._refs.ppSection.hidden = !showPp;

    if (!showPp) {
      if (this._layout === 'sheet' && this._openSheetName === 'pp') this._closeSheet();
      // Reset PP state when hidden
      this.state.passepartout = null;
      this.state.ppVariantId = null;
      this.state.ppPrice = 0;
      if (this._refs.ppLabel) this._refs.ppLabel.textContent = '—';
      if (this._refs.ppSurcharge) this._refs.ppSurcharge.textContent = '';
      this._updateSwatchPreview(this._refs.ppSwatchPreview, null);
    } else {
      if (!this.state.passepartout) {
        // Auto-select first PP when section becomes visible
        const firstPp = format.passepartouts[0];
        if (firstPp) this._selectPp(firstPp.handle);
      }
    }
  }

  /* ============================================================
     PASSEPARTOUT SELECTION
     ============================================================ */

  _selectPp(handle) {
    const format = this._getCurrentFormat();
    if (!format || !format.hasPassepartout) return;

    const pp = format.passepartouts.find(p => p.handle === handle);
    if (!pp) return;

    this.state.passepartout = handle;

    if (pp.handle === 'sin-paspartu') {
      this.state.ppVariantId = null;
      this.state.ppPrice = 0;
    } else {
      const pv = this._findAddonVariant(pp.variants, this.state.size);
      this.state.ppVariantId = pv ? pv.id : null;
      this.state.ppPrice = pv ? pv.price : 0;
    }

    this._updateSwatchSelection(this._refs.ppOptions, handle, 'pp');

    if (this._refs.ppLabel) {
      this._refs.ppLabel.textContent = pp.name;
    }
    this._updateSwatchPreview(this._refs.ppSwatchPreview, pp);

    if (this._refs.ppSurcharge) {
      this._refs.ppSurcharge.textContent = this.state.ppPrice > 0
        ? `+${this._formatMoney(this.state.ppPrice)}`
        : '';
    }

    this._updatePrice();
    this._updatePreview();
  }

  /* ============================================================
     RENDERING — Format tabs
     ============================================================ */

  _updateFormatTabs(handle) {
    [this, this._portal].filter(Boolean).forEach(root => {
      root.querySelectorAll('[data-format]').forEach(tab => {
        tab.setAttribute('aria-pressed', tab.dataset.format === handle ? 'true' : 'false');
      });
    });
    const fmt = this.config.formats.find(f => f.handle === handle);
    if (fmt) {
      if (this._refs.formatLabel) this._refs.formatLabel.textContent = fmt.name;
      if (this._refs.formatImg) {
        if (fmt.cardImage) {
          this._refs.formatImg.src = fmt.cardImage;
          this._refs.formatImg.alt = fmt.name;
          this._refs.formatImg.style.display = 'block';
        } else {
          this._refs.formatImg.src = '';
          this._refs.formatImg.style.display = 'none';
        }
      }
    }
  }

  /* ============================================================
     RENDERING — Size buttons
     ============================================================ */

  _renderSizes(format) {
    if (!this._refs.sizeOptions) return;

    const html = format.sizes.map(size => {
      const variant = this._findVariant(format._variantOptionValue || format.name, size);
      const available = variant ? variant.available : false;

      if (this._hideUnavailableSizes && !available) return '';

      const showPrice = this._showSizePrices || this._layout === 'sheet';
      let priceHtml = '';
      if (showPrice && variant) {
        const saleSpan = `<span class="pc__size-btn-price">${this._formatMoney(variant.price)}</span>`;
        if (this._layout === 'sheet' && variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHtml = `<span class="pc__size-btn-prices"><s class="pc__size-btn-compare">${this._formatMoney(variant.compare_at_price)}</s>${saleSpan}</span>`;
        } else {
          priceHtml = saleSpan;
        }
      }

      const displayLabel = this._layout === 'sheet' ? `${size} cm` : size;

      return `
        <button type="button" class="pc__size-btn" data-size="${size}"
          aria-pressed="false" ${!available ? 'disabled' : ''}>
          <span class="pc__size-btn-label">${displayLabel}</span>
          ${priceHtml}
        </button>
      `;
    }).join('');

    this._refs.sizeOptions.innerHTML = html;
  }

  _updateSizeButtons(selectedSize) {
    if (!this._refs.sizeOptions) return;

    this._refs.sizeOptions.querySelectorAll('[data-size]').forEach(btn => {
      btn.setAttribute('aria-pressed', btn.dataset.size === selectedSize ? 'true' : 'false');
    });
  }

  /* ============================================================
     RENDERING — Frame categories (filter pills)
     ============================================================ */

  _renderFrameCategories(format) {
    if (!this._refs.frameCategories) return;

    if (!format.frameCategories || format.frameCategories.length <= 1) {
      this._refs.frameCategories.innerHTML = '';
      this._refs.frameCategories.hidden = true;
      return;
    }

    this._refs.frameCategories.hidden = false;

    const html = [
      `<button type="button" class="pc__frame-cat-btn" data-frame-cat="all" aria-pressed="true">Todos</button>`
    ].concat(
      format.frameCategories.map(cat =>
        `<button type="button" class="pc__frame-cat-btn" data-frame-cat="${cat}" aria-pressed="false">${cat}</button>`
      )
    ).join('');

    this._refs.frameCategories.innerHTML = html;
    this._activeFrameCategory = null;
  }

  _filterFramesByCategory(cat) {
    if (!this._refs.frameCategories || !this._refs.frameOptions) return;

    // Update pill selection
    this._refs.frameCategories.querySelectorAll('[data-frame-cat]').forEach(btn => {
      btn.setAttribute('aria-pressed', btn.dataset.frameCat === cat ? 'true' : 'false');
    });

    this._activeFrameCategory = cat === 'all' ? null : cat;

    // Show/hide swatches
    this._refs.frameOptions.querySelectorAll('[data-frame]').forEach(swatch => {
      if (!this._activeFrameCategory) {
        swatch.style.display = '';
        return;
      }
      const frameCat = swatch.dataset.category || '';
      const isMatch = frameCat === this._activeFrameCategory || swatch.dataset.frame === 'sin-marco';
      swatch.style.display = isMatch ? '' : 'none';
    });
  }

  /* ============================================================
     RENDERING — Frame swatches
     ============================================================ */

  _renderFrames(format) {
    if (!this._refs.frameOptions) return;

    let html;

    if (this._layout === 'sheet') {
      html = format.frames.map(frame => {
        const isNone = frame.handle === 'sin-marco';
        const swatchStyle = !isNone
          ? (frame.swatchImage
            ? `background-image: url('${frame.swatchImage}');`
            : (frame.colorHex ? `background-color: ${frame.colorHex};` : ''))
          : '';

        const noneClass = isNone ? ' pc__sheet-option-swatch--none' : '';

        return `
          <button type="button" class="pc__sheet-option"
            data-frame="${frame.handle}"
            data-category="${frame.category || ''}"
            aria-pressed="false">
            <span class="pc__sheet-option-swatch pc__sheet-option-swatch--square${noneClass}" style="${swatchStyle}"></span>
            <span class="pc__sheet-option-name">${frame.name}</span>
            <span class="pc__sheet-option-price"></span>
          </button>
        `;
      }).join('');
    } else {
      html = format.frames.map(frame => {
        const isNone = frame.handle === 'sin-marco';
        const noneClass = isNone ? ' pc__swatch--none' : '';
        const bgStyle = !isNone ? `background-color: ${frame.colorHex};` : '';
        const imgStyle = frame.swatchImage ? `background-image: url('${frame.swatchImage}');` : '';

        return `
          <button type="button" class="pc__swatch${noneClass}"
            data-frame="${frame.handle}"
            data-category="${frame.category || ''}"
            aria-pressed="false" aria-label="${frame.name}">
            <span class="pc__swatch-inner" style="${bgStyle}${imgStyle}"></span>
            <span class="pc__swatch-label">${frame.name}</span>
          </button>
        `;
      }).join('');
    }

    this._refs.frameOptions.innerHTML = html;
  }

  /* ============================================================
     RENDERING — Passepartout swatches
     ============================================================ */

  _renderPassepartouts(format) {
    if (!this._refs.ppOptions || !this._refs.ppSection) return;

    if (!format.hasPassepartout || !format.passepartouts.length) {
      this._refs.ppSection.hidden = true;
      this.state.passepartout = null;
      this.state.ppVariantId = null;
      this.state.ppPrice = 0;
      return;
    }

    // Start hidden — _updatePpVisibility (called from _selectFrame) controls visibility
    this._refs.ppSection.hidden = true;

    let html;

    if (this._layout === 'sheet') {
      html = format.passepartouts.map(pp => {
        const isNone = pp.handle === 'sin-paspartu';
        const swatchStyle = !isNone
          ? (pp.swatchImage
            ? `background-image: url('${pp.swatchImage}');`
            : (pp.colorHex ? `background-color: ${pp.colorHex};` : ''))
          : '';

        const noneClass = isNone ? ' pc__sheet-option-swatch--none' : '';

        return `
          <button type="button" class="pc__sheet-option"
            data-pp="${pp.handle}"
            aria-pressed="false">
            <span class="pc__sheet-option-swatch${noneClass}" style="${swatchStyle}"></span>
            <span class="pc__sheet-option-name">${pp.name}</span>
            <span class="pc__sheet-option-price"></span>
          </button>
        `;
      }).join('');
    } else {
      html = format.passepartouts.map(pp => {
        const isNone = pp.handle === 'sin-paspartu';
        const noneClass = isNone ? ' pc__swatch--none' : '';
        const bgStyle = !isNone ? `background-color: ${pp.colorHex};` : '';
        const imgStyle = pp.swatchImage ? `background-image: url('${pp.swatchImage}');` : '';

        return `
          <button type="button" class="pc__swatch${noneClass}"
            data-pp="${pp.handle}"
            aria-pressed="false" aria-label="${pp.name}">
            <span class="pc__swatch-inner" style="${bgStyle}${imgStyle}"></span>
            <span class="pc__swatch-label">${pp.name}</span>
          </button>
        `;
      }).join('');
    }

    this._refs.ppOptions.innerHTML = html;
  }

  _updateSheetFramePrices(format) {
    if (this._layout !== 'sheet' || !this._refs.frameOptions || !this.state.size) return;

    this._refs.frameOptions.querySelectorAll('[data-frame]').forEach(btn => {
      const handle = btn.dataset.frame;
      if (handle === 'sin-marco') return;
      const frame = format.frames.find(f => f.handle === handle);
      if (!frame) return;
      const fv = this._findAddonVariant(frame.variants, this.state.size);
      const el = btn.querySelector('.pc__sheet-option-price');
      if (el) el.textContent = fv && fv.price > 0 ? `+${this._formatMoney(fv.price)}` : '';
    });
  }

  _updateSheetPpPrices(format) {
    if (this._layout !== 'sheet' || !this._refs.ppOptions || !this.state.size) return;

    this._refs.ppOptions.querySelectorAll('[data-pp]').forEach(btn => {
      const handle = btn.dataset.pp;
      if (handle === 'sin-paspartu') return;
      const pp = format.passepartouts.find(p => p.handle === handle);
      if (!pp) return;
      const pv = this._findAddonVariant(pp.variants, this.state.size);
      const el = btn.querySelector('.pc__sheet-option-price');
      if (el) el.textContent = pv && pv.price > 0 ? this._formatMoney(pv.price) : '';
    });
  }

  _updateSwatchSelection(container, handle, type) {
    if (!container) return;

    const attr = type === 'frame' ? 'data-frame' : 'data-pp';
    container.querySelectorAll(`[${attr}]`).forEach(swatch => {
      swatch.setAttribute('aria-pressed', swatch.getAttribute(attr) === handle ? 'true' : 'false');
    });
  }

  _updateSwatchPreview(el, item) {
    if (!el) return;
    if (!item || item.colorHex === 'transparent') {
      el.removeAttribute('style');
      return;
    }
    const bg = item.swatchImage
      ? `background-image: url('${item.swatchImage}'); background-size: cover;`
      : `background-color: ${item.colorHex};`;
    el.setAttribute('style', bg);
  }

  /* ============================================================
     VARIANT LOOKUP
     ============================================================ */

  _findVariant(formatValue, sizeValue) {
    const norm = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const normSize = s => norm(s).replace(/[\u00d7\u2715\u2716*]/g, 'x').replace(/\s*x\s*/g, 'x').replace(/\s*cm\s*$/, '').replace(/\s+/g, '');
    const fmtNorm = norm(formatValue);
    const sizeNorm = normSize(sizeValue);

    const variant = this.variants.find(v => {
      const opts = v.options;
      return norm(opts[0]) === fmtNorm && normSize(opts[1]) === sizeNorm;
    });

    if (!variant) {
      console.warn('[Configurator] No variant found for:', formatValue, sizeValue);
    }

    return variant;
  }

  _findAddonVariant(variants, size) {
    if (!variants || !variants.length || !size) return null;
    const norm = s => (s || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // strip accents
      .replace(/[\u00d7\u2715\u2716\u00D8*]/g, 'x')      // × ✕ ✖ and similar → x
      .replace(/\s*x\s*/gi, 'x')                          // collapse spaces around x
      .replace(/\s*cm\s*$/i, '')                           // remove trailing "cm"
      .replace(/\s+/g, '')                                 // strip all remaining whitespace
      .toLowerCase()
      .trim();
    const sizeNorm = norm(size);
    return variants.find(v => norm(v.size) === sizeNorm) || null;
  }

  _updateVariant() {
    const format = this._getCurrentFormat();
    if (!format || !this.state.size) return;

    const variant = this._findVariant(format._variantOptionValue || format.name, this.state.size);

    if (variant) {
      this.state.artworkVariantId = variant.id;
      this.state.artworkPrice = variant.price;

      if (this._refs.atcBtn) {
        this._refs.atcBtn.disabled = !variant.available;
      }
    } else {
      this.state.artworkVariantId = null;
      this.state.artworkPrice = 0;
    }

    if (this.state.frame && this.state.frame !== 'sin-marco') {
      const frame = format.frames.find(f => f.handle === this.state.frame);
      if (frame) {
        const fv = this._findAddonVariant(frame.variants, this.state.size);
        this.state.frameVariantId = fv ? fv.id : null;
        this.state.framePrice = fv ? fv.price : 0;
      }
    }

    if (format.hasPassepartout && this.state.passepartout && this.state.passepartout !== 'sin-paspartu') {
      const pp = format.passepartouts.find(p => p.handle === this.state.passepartout);
      if (pp) {
        const pv = this._findAddonVariant(pp.variants, this.state.size);
        this.state.ppVariantId = pv ? pv.id : null;
        this.state.ppPrice = pv ? pv.price : 0;
      }
    }
  }

  /* ============================================================
     PRICE CALCULATION
     ============================================================ */

  _updatePrice() {
    const total = this.state.artworkPrice + this.state.framePrice + this.state.ppPrice;

    // ATC button price
    if (this._refs.atcPrice) {
      this._refs.atcPrice.textContent = total > 0 ? `· ${this._formatMoney(total)}` : '';
    }

    // Update surcharges in drawer triggers
    if (this._refs.frameSurcharge) {
      this._refs.frameSurcharge.textContent = this.state.framePrice > 0
        ? `+${this._formatMoney(this.state.framePrice)}`
        : '';
    }

    if (this._refs.ppSurcharge) {
      this._refs.ppSurcharge.textContent = this.state.ppPrice > 0
        ? `+${this._formatMoney(this.state.ppPrice)}`
        : '';
    }

    // Sync native Price block when breakdown is hidden
    if (this._nativeSalePrice && total > 0) {
      this._nativeSalePrice.textContent = this._formatMoney(total);
    }

    // Sync the quick-buy drawer header price, preserving its accessible label span.
    if (this._qbHeaderPrice && total > 0) {
      Array.from(this._qbHeaderPrice.childNodes).forEach(n => {
        if (n.nodeType === 3) this._qbHeaderPrice.removeChild(n);
      });
      this._qbHeaderPrice.appendChild(document.createTextNode(this._formatMoney(total)));
    }

    this._renderBreakdown();
  }

  _renderBreakdown() {
    if (!this._refs.breakdown) return;

    const total = this.state.artworkPrice + this.state.framePrice + this.state.ppPrice;
    const hasFrame = this.state.framePrice > 0;
    const hasPp = this.state.ppPrice > 0;
    const showDetails = this._showBreakdown && (hasFrame || hasPp);

    // When breakdown is disabled, hide the entire element
    if (!this._showBreakdown) {
      this._refs.breakdown.hidden = true;
      return;
    }

    // Total section is ALWAYS visible when there's a price
    this._refs.breakdown.hidden = total <= 0;

    // Detail rows only visible when setting is enabled AND there are extras
    if (this._refs.breakdownArtRow) {
      this._refs.breakdownArtRow.hidden = !showDetails;
    }
    if (this._refs.breakdownArtPrice) {
      this._refs.breakdownArtPrice.textContent = this._formatMoney(this.state.artworkPrice);
    }

    if (this._refs.breakdownFrameRow) {
      this._refs.breakdownFrameRow.hidden = !showDetails || !hasFrame;
    }
    if (hasFrame && this._refs.breakdownFrameLabel) {
      const format = this._getCurrentFormat();
      const frame = format?.frames.find(f => f.handle === this.state.frame);
      this._refs.breakdownFrameLabel.textContent = frame ? frame.name : 'Marco';
    }
    if (hasFrame && this._refs.breakdownFramePrice) {
      this._refs.breakdownFramePrice.textContent = this._formatMoney(this.state.framePrice);
    }

    if (this._refs.breakdownPpRow) {
      this._refs.breakdownPpRow.hidden = !showDetails || !hasPp;
    }
    if (hasPp && this._refs.breakdownPpLabel) {
      const format = this._getCurrentFormat();
      const pp = format?.passepartouts.find(p => p.handle === this.state.passepartout);
      this._refs.breakdownPpLabel.textContent = pp ? pp.name : 'Paspartú';
    }
    if (hasPp && this._refs.breakdownPpPrice) {
      this._refs.breakdownPpPrice.textContent = this._formatMoney(this.state.ppPrice);
    }

    if (this._refs.breakdownTotal) {
      this._refs.breakdownTotal.textContent = this._formatMoney(total);
    }
  }

  /**
   * Format price in cents to display string.
   */
  _formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const shopMoney = window.Shopify?.currency?.active || 'EUR';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
        style: 'currency',
        currency: shopMoney,
        minimumFractionDigits: 2
      }).format(amount);
    } catch {
      return `${amount} €`;
    }
  }

  /* ============================================================
     ADD TO CART — Multi-item via /cart/add.js
     ============================================================ */

  async _addToCart() {
    if (this._adding || !this.state.artworkVariantId) return;

    this._adding = true;
    const btn = this._refs.atcBtn;
    if (btn) {
      btn.setAttribute('data-loading', '');
      btn.setAttribute('aria-busy', 'true');
    }

    const bundleId = `${this.config.productHandle}-${Date.now()}`;
    const format = this._getCurrentFormat();
    const items = [];

    items.push({
      id: this.state.artworkVariantId,
      quantity: 1,
      properties: {
        '_bundle_id': bundleId,
        '_role': 'artwork',
        [this.optionFormatName]: format ? format.name : '',
        [this.optionSizeName]: this.state.size || ''
      }
    });

    if (this.state.frameVariantId) {
      items.push({
        id: this.state.frameVariantId,
        quantity: 1,
        parent_id: this.state.artworkVariantId,
        properties: {
          '_bundle_id': bundleId,
          '_role': 'frame',
          '_parent': this.config.productHandle
        }
      });
    }

    if (this.state.ppVariantId) {
      items.push({
        id: this.state.ppVariantId,
        quantity: 1,
        parent_id: this.state.artworkVariantId,
        properties: {
          '_bundle_id': bundleId,
          '_role': 'passepartout',
          '_parent': this.config.productHandle
        }
      });
    }

    try {
      // Prepare bundled sections (same as native ProductForm)
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
          sections_url: `${Shopify.routes.root}variants/${this.state.artworkVariantId}`
        })
      });

      if (btn) btn.removeAttribute('aria-busy');

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || 'Error adding to cart');
      }

      // Follow the exact same pattern as native ProductForm
      if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      // Fetch full cart for event payload
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent['sections'] = responseJson['sections'];

      const addedItems = responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson];

      // Dispatch native variant:add event on the product form (if present)
      const nativeForm = this._nativeFormId ? document.forms[this._nativeFormId] : null;
      const eventTarget = nativeForm || this;
      eventTarget.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: addedItems, cart: cartContent }
      }));

      // Dispatch native cart:change on documentElement (opens cart drawer, updates count)
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cartContent }
      }));

    } catch (err) {
      console.error('[Configurator] Add to cart failed:', err);

      // Dispatch native cart:error on the product form
      const nativeForm = this._nativeFormId ? document.forms[this._nativeFormId] : null;
      const eventTarget = nativeForm || this;
      eventTarget.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: err.message }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

    } finally {
      this._adding = false;
      if (btn) {
        btn.removeAttribute('data-loading');
        btn.removeAttribute('aria-busy');
      }
    }
  }

  /* ============================================================
     HELPERS
     ============================================================ */

  /**
   * Normalize a size list from metaobject config (array or comma string) into
   * a clean array with × → x normalization.
   */
  _normalizeSizeList(raw) {
    if (!raw) return [];
    const normChar = s => (typeof s === 'string' ? s : String(s)).replace(/\u00d7/g, 'x').trim();
    if (Array.isArray(raw)) return raw.map(normChar).filter(Boolean);
    if (typeof raw === 'string') return raw.split(',').map(normChar).filter(Boolean);
    return [];
  }

  /**
   * Extract unique sizes from product variants for a given format name.
   * Reads options[0] (format) and options[1] (size) from the variant data.
   * Returns sizes in the order they appear in the variants.
   */
  _extractSizesFromVariants(formatName) {
    if (!this.variants || !this.variants.length) return [];

    const norm = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const normSize = s => norm(s).replace(/[\u00d7\u2715\u2716*]/g, 'x').replace(/\s*x\s*/g, 'x').replace(/\s*cm\s*$/i, '').replace(/\s+/g, '');
    const fmtNorm = norm(formatName);

    const seen = new Set();
    const sizes = [];

    for (const v of this.variants) {
      const opts = v.options;
      if (!opts || opts.length < 2) continue;
      if (norm(opts[0]) !== fmtNorm) continue;

      const sizeRaw = (typeof opts[1] === 'string' ? opts[1] : String(opts[1])).replace(/\u00d7/g, 'x').trim();
      const sizeKey = normSize(sizeRaw);

      if (sizeKey && !seen.has(sizeKey)) {
        seen.add(sizeKey);
        sizes.push(sizeRaw);
      }
    }

    return sizes;
  }

  _getCurrentFormat() {
    if (!this.state.format || !this.config) return null;
    return this.config.formats.find(f => f.handle === this.state.format);
  }

  /**
   * Pick the orientation-specific variant of an image field.
   * E.g. for 'overlayImage' on a horizontal product, tries 'overlayImageH' first,
   * then falls back to 'overlayImage' (vertical default).
   */
  _getOrientedImage(obj, baseField) {
    if (!obj) return '';
    const suffix = this._orientation === 'horizontal' ? 'H'
                 : this._orientation === 'square' ? 'S'
                 : '';
    if (suffix) {
      const oriented = obj[baseField + suffix];
      if (oriented) return oriented;
    }
    return obj[baseField] || '';
  }

  /* ============================================================
     MOCKUP PREVIEW — overlay layers in gallery
     ============================================================ */

  _updatePreview() {
    if (!this._mockup) return;

    const format = this._getCurrentFormat();
    if (!format) return;

    // Determine if PP is active (format supports PP and a real PP is selected)
    const ppActive = format.hasPassepartout
      && this.state.passepartout
      && this.state.passepartout !== 'sin-paspartu';

    const hasFrame = this.state.frame && this.state.frame !== 'sin-marco';

    // Swap base artwork image based on format type + frame/PP state
    if (this._mockupBase) {
      const ft = (format.formatType || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const sh = (format.systemHandle || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/-\d+$/, '');
      let targetSrc;
      if (ft === 'lienzo' || sh === 'lienzo' || sh.includes('lienzo')) {
        targetSrc = hasFrame ? this._srcLienzoMarco : this._srcLienzo;
      } else {
        // Lámina (default)
        if (hasFrame && ppActive) targetSrc = this._srcConPp;
        else targetSrc = this._srcSinPp;
      }
      // Fallback chain: sin-pp → initial
      targetSrc = targetSrc || this._srcSinPp || this._srcInitial;
      if (targetSrc && this._mockupBase.src !== targetSrc) {
        this._mockupBase.src = targetSrc;
      }
    }

    // Frame overlay — pick orientation-specific image
    const frame = format.frames.find(f => f.handle === this.state.frame);
    const frameOverlay = frame ? this._getOrientedImage(frame, 'overlayImage') : '';
    if (hasFrame && frameOverlay) {
      this._mockupFrame.src = frameOverlay;
      this._mockupFrame.classList.add('is-visible');
    } else {
      // No frame selected — show format base overlay if available (e.g. Lámina white border)
      const baseOverlay = this._getOrientedImage(format, 'baseOverlay');
      if (baseOverlay) {
        this._mockupFrame.src = baseOverlay;
        this._mockupFrame.classList.add('is-visible');
      } else {
        this._mockupFrame.classList.remove('is-visible');
      }
    }

    // PP overlay — pick orientation-specific image
    const pp = format.passepartouts.find(p => p.handle === this.state.passepartout);
    const ppOverlay = pp ? this._getOrientedImage(pp, 'overlayImage') : '';
    if (ppActive && pp && ppOverlay) {
      this._mockupPp.src = ppOverlay;
      this._mockupPp.classList.add('is-visible');
    } else {
      this._mockupPp.classList.remove('is-visible');
    }

    // Scroll gallery to mockup on first interaction
    this._scrollToMockup();
  }

  _scrollToMockup() {
    if (this._mockupScrolled || !this._mockupCarousel) return;
    this._mockupScrolled = true;

    // Use the carousel's native select method if available
    if (typeof this._mockupCarousel.select === 'function') {
      this._mockupCarousel.select(0, { animate: false });
    } else {
      this._mockup.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfigurator);
}
